import { useTranslation } from 'react-i18next';
import type { PerioDiagnosis } from '@/utils/perio';

// ── Stage / Grade chip colors ─────────────────────────────────────────────────

const stageColors: Record<string, string> = {
  I:   'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  II:  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  III: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  IV:  'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

const gradeColors: Record<string, string> = {
  A: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  B: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
  C: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

// ── Props ─────────────────────────────────────────────────────────────────────

interface PerioDiagnosisCardProps {
  diagnosis: PerioDiagnosis;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PerioDiagnosisCard({ diagnosis }: PerioDiagnosisCardProps) {
  const { t } = useTranslation();

  if (!diagnosis.stage) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
          {t('dental.perio.diagnosis.title')}
        </p>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {t('dental.perio.diagnosis.title', 'Ingen parodontale problemer registreret.')}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {t('dental.perio.diagnosis.title')}
      </p>

      <div className="flex flex-wrap gap-2">
        {diagnosis.stage && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {t('dental.perio.diagnosis.stage')}
            </span>
            <span
              className={`rounded-full px-2.5 py-0.5 text-sm font-bold ${stageColors[diagnosis.stage] ?? ''}`}
            >
              {diagnosis.stage}
            </span>
          </div>
        )}

        {diagnosis.grade && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {t('dental.perio.diagnosis.grade')}
            </span>
            <span
              className={`rounded-full px-2.5 py-0.5 text-sm font-bold ${gradeColors[diagnosis.grade] ?? ''}`}
            >
              {diagnosis.grade}
              {diagnosis.gradeIsDefault && (
                <span className="ml-0.5 text-xs font-normal opacity-70">*</span>
              )}
            </span>
          </div>
        )}
      </div>

      {diagnosis.summary && (
        <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">{diagnosis.summary}</p>
      )}

      <p className="mt-3 rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
        {t('dental.perio.diagnosis.disclaimer')}
      </p>
    </div>
  );
}
